import React from 'react';
import { Box, Typography, Alert, List, ListItem, ListItemText, Divider } from '@mui/material';

/** Shared legal sections for web + copy alignment with Flutter `terms_widget.dart` */
export const TERMS_SECTIONS = [
  {
    title: '1. Fees & refund policy',
    bullets: [
      'All programme and course fees are non-refundable under any circumstances once paid.',
      'No refund is given for partial usage, non-attendance, change of mind, or dissatisfaction.',
      'Administrative or processing charges, if any, are also non-refundable.',
    ],
  },
  {
    title: '2. Course access',
    bullets: [
      'Access is granted for a limited validity period as configured for your enrolment.',
      'Sharing login credentials or account access with others is strictly prohibited.',
      'KNLS may suspend or terminate access for breach of these terms.',
    ],
  },
  {
    title: '3. Intellectual property',
    bullets: [
      'All learning materials, videos, assessments, branding, and platform content remain the property of KNLS.',
      'Copying, redistribution, resale, or public display of content without written permission is not allowed.',
    ],
  },
  {
    title: '4. Certification',
    bullets: [
      'Certificates or completions, where offered, are based on completion criteria defined for each programme.',
      'KNLS does not guarantee employment, salary, or third-party recognition.',
    ],
  },
  {
    title: '5. Technical responsibility',
    bullets: [
      'You are responsible for a compatible device, browser or app version, and a stable internet connection.',
      'KNLS is not liable for losses caused by connectivity, device failure, or third-party service outages.',
    ],
  },
  {
    title: '6. Code of conduct',
    bullets: [
      'You must not misuse the platform, harass others, attempt unauthorised access, or engage in illegal activity.',
      'Automated scraping, reverse engineering, or circumvention of technical controls is prohibited.',
    ],
  },
  {
    title: '7. Updates',
    bullets: [
      'KNLS may update fees, course structure, content, policies, or platform features at any time.',
      'Continued use after changes constitutes acceptance of the updated terms where permitted by law.',
    ],
  },
  {
    title: '8. Legal',
    bullets: [
      'KNLS registered / principal place of business for these terms: Chintalapudi – 534460, Eluru District, Andhra Pradesh, India.',
      'These terms are governed by the laws of India and the State of Andhra Pradesh, without prejudice to any mandatory consumer protections that cannot be waived.',
      'Subject to applicable law, you agree that courts having territorial jurisdiction over Chintalapudi and/or Eluru District, Andhra Pradesh, shall have jurisdiction over disputes arising from these terms or your use of the platform.',
    ],
  },
];

/**
 * @param {{ dense?: boolean, showWarning?: boolean }} props
 */
export default function TermsSection({ dense = false, showWarning = true }) {
  const spacing = dense ? 1.5 : 2;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {showWarning && (
        <Alert severity="warning" sx={{ mb: 3, fontWeight: 600 }}>
          ⚠️ Fees once paid are non-refundable.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" paragraph>
        Welcome to the KNLS learning platform. By registering, paying fees, or using courses, you agree to the
        following terms, conditions, and disclaimers.
      </Typography>

      {TERMS_SECTIONS.map((section) => (
        <Box key={section.title} sx={{ mb: spacing }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700, fontSize: dense ? '1rem' : undefined }}>
            {section.title}
          </Typography>
          <List dense={dense} disablePadding>
            {section.bullets.map((line) => (
              <ListItem key={line} alignItems="flex-start" sx={{ py: 0.25, pl: 0 }}>
                <ListItemText
                  primaryTypographyProps={{ variant: dense ? 'body2' : 'body1' }}
                  primary={<span>• {line}</span>}
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ mt: 2 }} />
        </Box>
      ))}
    </Box>
  );
}
